import { escapeHtmlAttribute } from "./escapeHtml";
import type { MenuSettings, PlayerSetting } from "./menuSettings";
import {
  ASSISTANT_ICON_SVG,
  HOME_ICON_SVG,
  MENU_ICON_SVG,
  OPTIONS_ICON_SVG,
  OTHER_GAMES_ICON_SVG,
  RULES_ICON_SVG,
} from "./cornerIcons";
import { isFullscreen, isMobileDevice, requestFullscreen, toggleFullscreen } from "./fullscreen";
import { goHome } from "./goHome";
import { showOptionsModal } from "./optionsModal";
import { showRulesModal } from "./rulesModal";
import { showOtherGamesModal } from "./otherGamesModal";
import { showDifficultyModal, type TutorialDifficulty } from "./difficultyModal";
import type { AiDifficulty } from "../game/ai";
import { TIME_CONTROL_PRESETS } from "../game/clock";
import type { Color } from "../game/types";
import { sound } from "../audio/sound";
import logoUrl from "../assets/ui/logo.webp";
import backgroundUrl from "../assets/background.jpg";

export interface MenuResult {
  white: PlayerSetting;
  black: PlayerSetting;
  timeControlId: string;
}

export type TutorialKind = "rules" | "strategy" | "exercises";

/** Titre affiche dans la modale de choix de niveau, selon le parcours demande. */
const DIFFICULTY_MODAL_TITLE: Record<"strategy" | "exercises", string> = {
  strategy: "Strategies du Taverneu",
  exercises: "Exercices de strategie",
};

const DOT_COLOR: Record<Color, string> = { w: "#ece0c4", b: "#241609" };
const ROW_LABEL: Record<Color, string> = { w: "Blancs", b: "Noirs" };

function playerRowHtml(color: Color, player: PlayerSetting): string {
  return `
    <div class="player-row" data-color="${color}">
      <span class="player-dot" style="background:${DOT_COLOR[color]}"></span>
      <input
        type="text"
        class="player-name-input"
        data-color="${color}"
        maxlength="18"
        placeholder="${ROW_LABEL[color]}"
        value="${escapeHtmlAttribute(player.name)}"
      />
      <div class="segmented player-type-toggle" data-color="${color}">
        <button type="button" data-type="human">Humain</button>
        <button type="button" data-type="ai">Bot</button>
      </div>
      <select class="player-difficulty" data-color="${color}" hidden>
        <option value="easy">Facile</option>
        <option value="normal">Normal</option>
        <option value="hard">Difficile</option>
      </select>
    </div>
  `;
}

function timeControlFieldHtml(selectedId: string): string {
  const options = TIME_CONTROL_PRESETS.map(
    (preset) => `<option value="${preset.id}" ${preset.id === selectedId ? "selected" : ""}>${preset.label}</option>`,
  ).join("");
  return `
    <div class="field">
      <label for="time-control-select">Chrono</label>
      <select id="time-control-select">${options}</select>
    </div>
  `;
}

/**
 * Affiche le menu de demarrage. `initial` prefiltre les champs (retrouves
 * dans localStorage) pour que les joueurs n'aient pas a tout retaper a
 * chaque partie. Un seul ecran, une ligne par joueur (nom + Humain/Bot),
 * pas de bascule de "mode" separee. Ne retire pas l'ecran elle-meme : le
 * controleur s'en charge apres le fondu au noir, pour laisser le temps a
 * la transition de la masquer. `onStartTutorial` est appele directement
 * (sans resoudre la promesse retournee, qui reste en attente indefiniment
 * dans ce cas) : c'est au controleur de relancer le menu une fois le tuto
 * termine. `onPlayOnline` suit exactement la meme convention. Pour "strategy"
 * et "exercises", un choix de niveau (`showDifficultyModal`) precede l'appel ;
 * annuler ce choix laisse simplement le menu en place. `difficulty` vaut
 * "debutant" par convention pour "rules" (le tutoriel des regles de base
 * n'est pas filtre par niveau, la valeur y est donc ignoree).
 */
export function showMenu(
  initial: MenuSettings,
  onStartTutorial: (kind: TutorialKind, difficulty: TutorialDifficulty) => void,
  onPlayOnline: () => void,
): Promise<MenuResult> {
  return new Promise((resolve) => {
    const screen = document.createElement("div");
    screen.className = "menu-screen interactive";
    screen.style.backgroundImage = `radial-gradient(ellipse at center, rgba(10, 5, 2, 0.55), rgba(3, 1, 0, 0.88)), url(${backgroundUrl})`;
    screen.innerHTML = `
      <div class="menu-card">
        <h1 class="menu-logo-wrap"><img class="menu-logo" src="${logoUrl}" alt="Echec et Mat du Taverneux" /></h1>
        <p class="subtitle">A deux joueurs, humains ou bots</p>

        ${playerRowHtml("w", initial.white)}
        ${playerRowHtml("b", initial.black)}
        ${timeControlFieldHtml(initial.timeControlId)}

        <div class="menu-actions">
          <button class="btn btn-primary" id="start-btn" type="button">Commencer la partie</button>
          <button class="btn" id="online-btn" type="button">Jouer en ligne</button>
          <button class="btn btn-ghost" id="tutorial-btn" type="button">Tutoriel du Taverneu</button>
          <button class="btn btn-ghost" id="strategy-tutorial-btn" type="button">Strategies du Taverneu</button>
          <button class="btn btn-ghost" id="exercises-btn" type="button">Exercices de strategie</button>
        </div>
      </div>
      <div class="corner-buttons">
        <div class="corner-buttons-list" id="menu-corner-buttons-list">
          <button class="btn-corner" id="menu-fullscreen-btn" type="button">
            <span class="ico">⛶</span><span class="lbl">Plein ecran</span>
          </button>
          <button class="btn-corner" id="menu-options-btn" type="button">
            <span class="ico">${OPTIONS_ICON_SVG}</span><span class="lbl">Options</span>
          </button>
          <button class="btn-corner" id="menu-assistant-btn" type="button" disabled title="Disponible en partie">
            <span class="ico">${ASSISTANT_ICON_SVG}</span><span class="lbl">Assistant</span>
          </button>
          <button class="btn-corner" id="menu-rules-btn" type="button">
            <span class="ico">${RULES_ICON_SVG}</span><span class="lbl">Regles</span>
          </button>
          <button class="btn-corner" id="menu-other-games-btn" type="button">
            <span class="ico">${OTHER_GAMES_ICON_SVG}</span><span class="lbl">Autres jeux</span>
          </button>
          <a class="btn-corner" id="menu-home-btn" href="/" title="Accueil">
            <span class="ico">${HOME_ICON_SVG}</span><span class="lbl">Accueil</span>
          </a>
        </div>
        <button class="btn-corner corner-toggle-btn" id="menu-corner-toggle-btn" type="button" aria-expanded="false" aria-label="Options">
          <span class="ico">${MENU_ICON_SVG}</span>
        </button>
      </div>
    `;
    document.getElementById("ui-root")?.appendChild(screen);

    const cornerToggleBtn = screen.querySelector<HTMLButtonElement>("#menu-corner-toggle-btn")!;
    const cornerButtonsList = screen.querySelector<HTMLDivElement>("#menu-corner-buttons-list")!;
    cornerToggleBtn.addEventListener("click", () => {
      const open = cornerButtonsList.classList.toggle("open");
      cornerToggleBtn.setAttribute("aria-expanded", String(open));
    });

    const fullscreenBtn = screen.querySelector<HTMLButtonElement>("#menu-fullscreen-btn")!;
    // Reflete l'etat courant (ex: retour au menu apres une partie passee en plein ecran auto sur mobile).
    fullscreenBtn.querySelector(".lbl")!.textContent = isFullscreen() ? "Quitter le plein ecran" : "Plein ecran";
    fullscreenBtn.addEventListener("click", () => {
      void toggleFullscreen().then((on) => {
        fullscreenBtn.querySelector(".lbl")!.textContent = on ? "Quitter le plein ecran" : "Plein ecran";
      });
    });

    screen.querySelector<HTMLButtonElement>("#menu-options-btn")?.addEventListener("click", () => showOptionsModal());
    screen.querySelector<HTMLButtonElement>("#menu-rules-btn")?.addEventListener("click", () => showRulesModal());
    screen
      .querySelector<HTMLButtonElement>("#menu-other-games-btn")
      ?.addEventListener("click", () => showOtherGamesModal());
    // Le lien garde son href="/" natif comme repli (clic du milieu, JS coupe) ;
    // goHome() gere le cas iframe de l'accueil (fermeture via postMessage).
    screen.querySelector<HTMLAnchorElement>("#menu-home-btn")?.addEventListener("click", (event) => {
      event.preventDefault();
      goHome();
    });
    // "Assistant" est present pour que les deux menus sandwich (accueil et en jeu) offrent
    // les memes entrees, mais il n'a aucun plateau a analyser tant qu'aucune partie n'a
    // commence : il reste donc desactive ici (cf. `title` explicatif).

    screen.querySelector<HTMLButtonElement>("#online-btn")?.addEventListener("click", () => {
      // Meme convention que les tutoriels : on retire l'ecran sans resoudre la promesse,
      // c'est au controleur de relancer le menu si le joueur quitte le salon en ligne.
      if (isMobileDevice()) void requestFullscreen();
      sound.ensureStarted();
      screen.remove();
      onPlayOnline();
    });

    screen.querySelector<HTMLButtonElement>("#tutorial-btn")?.addEventListener("click", () => {
      sound.ensureStarted();
      screen.remove();
      onStartTutorial("rules", "debutant");
    });
    const startWithDifficulty = (kind: "strategy" | "exercises"): void => {
      void showDifficultyModal(DIFFICULTY_MODAL_TITLE[kind]).then((difficulty) => {
        if (!difficulty) return;
        sound.ensureStarted();
        screen.remove();
        onStartTutorial(kind, difficulty);
      });
    };
    screen.querySelector<HTMLButtonElement>("#strategy-tutorial-btn")?.addEventListener("click", () => {
      startWithDifficulty("strategy");
    });
    screen.querySelector<HTMLButtonElement>("#exercises-btn")?.addEventListener("click", () => {
      startWithDifficulty("exercises");
    });

    let timeControlId = initial.timeControlId;
    screen.querySelector<HTMLSelectElement>("#time-control-select")?.addEventListener("change", (e) => {
      timeControlId = (e.target as HTMLSelectElement).value;
    });

    const players: Record<Color, PlayerSetting> = {
      w: { ...initial.white },
      b: { ...initial.black },
    };

    function refreshRow(color: Color): void {
      const player = players[color];
      const toggle = screen.querySelector<HTMLDivElement>(`.player-type-toggle[data-color="${color}"]`)!;
      toggle.querySelectorAll<HTMLButtonElement>("button").forEach((btn) => {
        btn.classList.toggle("active", (btn.dataset.type === "ai") === player.isAI);
      });
      const nameInput = screen.querySelector<HTMLInputElement>(`.player-name-input[data-color="${color}"]`)!;
      nameInput.disabled = player.isAI;
      if (player.isAI && (nameInput.value === "Blancs" || nameInput.value === "Noirs" || nameInput.value.trim() === "")) {
        nameInput.value = "Ordinateur";
        player.name = "Ordinateur";
      }
      const select = screen.querySelector<HTMLSelectElement>(`select.player-difficulty[data-color="${color}"]`)!;
      select.hidden = !player.isAI;
      select.value = player.difficulty;
    }

    screen.querySelectorAll<HTMLDivElement>(".player-type-toggle").forEach((toggle) => {
      const color = toggle.dataset.color as Color;
      toggle.querySelectorAll<HTMLButtonElement>("button").forEach((btn) => {
        btn.addEventListener("click", () => {
          const wasAI = players[color].isAI;
          players[color].isAI = btn.dataset.type === "ai";
          if (!wasAI && players[color].isAI) {
            // On quitte le champ nom humain pour un bot : on vide le nom par defaut pour le remplacer.
            const nameInput = screen.querySelector<HTMLInputElement>(`.player-name-input[data-color="${color}"]`)!;
            if (nameInput.value.trim() === ROW_LABEL[color]) nameInput.value = "";
          } else if (wasAI && !players[color].isAI && players[color].name === "Ordinateur") {
            players[color].name = ROW_LABEL[color];
          }
          refreshRow(color);
        });
      });
    });

    screen.querySelectorAll<HTMLInputElement>(".player-name-input").forEach((input) => {
      const color = input.dataset.color as Color;
      input.addEventListener("input", () => {
        players[color].name = input.value;
      });
    });

    screen.querySelectorAll<HTMLSelectElement>("select.player-difficulty").forEach((select) => {
      const color = select.dataset.color as Color;
      select.addEventListener("change", () => {
        players[color].difficulty = select.value as AiDifficulty;
      });
    });

    refreshRow("w");
    refreshRow("b");

    const startBtn = screen.querySelector<HTMLButtonElement>("#start-btn")!;
    startBtn.addEventListener("click", () => {
      // Plein ecran / audio : doivent rester dans la pile synchrone du clic (aucun await
      // avant), sans quoi certains navigateurs (surtout iOS/Safari) refusent la demande
      // une fois le geste utilisateur "refroidi".
      if (isMobileDevice()) void requestFullscreen();
      sound.ensureStarted();
      const finalize = (color: Color): PlayerSetting => {
        const p = players[color];
        const name = p.isAI ? p.name.trim() || "Ordinateur" : p.name.trim() || ROW_LABEL[color];
        return { ...p, name };
      };
      resolve({ white: finalize("w"), black: finalize("b"), timeControlId });
    });
  });
}
